import { useState, useEffect } from 'react';
import { api } from './lib/api';

interface SyncStatus {
  id: string;
  banque: string;
  iban: string;
  sync_provider: string | null;
  last_sync_at: string | null;
}

interface Props {
  entrepriseId: string;
  exerciceId: string;
}

export default function BankSync({ entrepriseId, exerciceId }: Props) {
  const [accounts, setAccounts] = useState<SyncStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<any>(null);

  const [connectCompteId, setConnectCompteId] = useState('');
  const [connectProvider, setConnectProvider] = useState<'bridge' | 'budgetinsight'>('bridge');
  const [connectToken, setConnectToken] = useState('');

  useEffect(() => { loadStatus(); }, [entrepriseId]);

  const loadStatus = async () => {
    try {
      const data = await api.bankSync.status(entrepriseId);
      setAccounts(data);
    } catch {}
  };

  const connectAccount = async () => {
    if (!connectCompteId || !connectToken) return;
    setLoading(true);
    setError(null);
    try {
      await api.bankSync.connect({
        entreprise_id: entrepriseId,
        compte_id: connectCompteId,
        provider: connectProvider,
        access_token: connectToken,
      });
      setConnectToken('');
      loadStatus();
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const syncAccount = async (compteId: string) => {
    setLoading(true);
    setError(null);
    setSyncResult(null);
    try {
      const data = await api.bankSync.sync(compteId, { entreprise_id: entrepriseId, exercice_id: exerciceId });
      setSyncResult(data);
      loadStatus();
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Synchronisation bancaire (Open Banking)</h2>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>}

      <h3 className="text-xl font-semibold text-gray-800 mb-4">Comptes bancaires</h3>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Banque</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">IBAN</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Provider</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Dernière sync</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{a.banque}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{a.iban}</td>
                <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{a.sync_provider || '—'}</td>
                <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{a.last_sync_at ? new Date(a.last_sync_at).toLocaleString('fr-FR') : '—'}</td>
                <td className="px-4 py-3 border-b border-gray-100">
                  {a.sync_provider ? (
                    <button onClick={() => syncAccount(a.id)} disabled={loading} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
                      {loading ? '...' : 'Synchroniser'}
                    </button>
                  ) : (
                    <button onClick={() => setConnectCompteId(a.id)} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors">Connecter</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {connectCompteId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Connecter le compte</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>Provider</label>
              <select value={connectProvider} onChange={e => setConnectProvider(e.target.value as any)} className={inputClass}>
                <option value="bridge">Bridge (Bankin')</option>
                <option value="budgetinsight">Budget Insight</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Token d'accès</label>
              <input value={connectToken} onChange={e => setConnectToken(e.target.value)} placeholder="access_token" className={inputClass} />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setConnectCompteId('')} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
            <button onClick={connectAccount} disabled={loading || !connectToken} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Connecter</button>
          </div>
        </div>
      )}

      {syncResult && (
        <div className="text-green-700 bg-green-50 border border-green-200 px-4 py-3 rounded-lg text-sm">
          <h4 className="font-semibold mb-1">Résultat de la synchronisation</h4>
          <p>Provider : {syncResult.provider}</p>
          <p>{syncResult.transactions.length} transaction(s) récupérée(s)</p>
          <p>{syncResult.created_ecritures} écriture(s) créée(s) en brouillon</p>
        </div>
      )}
    </div>
  );
}
